import React, { useState } from 'react';
import TablePagination from '../components/TablePagination';
import { useTablePagination } from '../hooks/useTablePagination';

type DeviceStatus = '启用' | '禁用';
type DeviceWorkStatus = '忙碌' | '闲置';
type DeviceOnlineStatus = '在线' | '离线';

interface SipDevice {
  id: string;
  name: string;
  workStatus: DeviceWorkStatus;
  status: DeviceStatus;
  onlineStatus: DeviceOnlineStatus;
  address: string;
  createTime: string;
  lastLeaveMeetingTime: string;
  lastSeen: string;
}

interface FilterState {
  keyword: string;
}

interface RenameModalState {
  id: string;
  name: string;
}

const formatNow = () => new Date().toLocaleString('zh-CN', { hour12: false });

const initialFilter: FilterState = {
  keyword: ''
};

const defaultDevices: SipDevice[] = [
  {
    id: 'sip-001',
    name: '主会场终端-A',
    workStatus: '忙碌',
    status: '启用',
    onlineStatus: '在线',
    address: '192.168.10.21:5060',
    createTime: '2024-01-12 09:30',
    lastLeaveMeetingTime: '2024-01-15 10:10',
    lastSeen: '2024-01-15 10:28'
  },
  {
    id: 'sip-002',
    name: '分会场终端-B',
    workStatus: '闲置',
    status: '禁用',
    onlineStatus: '离线',
    address: '192.168.10.35:5061',
    createTime: '2024-01-10 16:10',
    lastLeaveMeetingTime: '2024-01-15 08:45',
    lastSeen: '—'
  },
  {
    id: 'sip-003',
    name: '成都分部-天府厅',
    workStatus: '忙碌',
    status: '启用',
    onlineStatus: '在线',
    address: '10.30.12.21:5060',
    createTime: '2026-01-06 09:20',
    lastLeaveMeetingTime: '2026-02-26 16:10',
    lastSeen: '2026-02-26 16:25'
  },
  {
    id: 'sip-004',
    name: '重庆客户支持-501',
    workStatus: '闲置',
    status: '启用',
    onlineStatus: '在线',
    address: '10.22.50.11:5060',
    createTime: '2026-01-09 11:40',
    lastLeaveMeetingTime: '2026-02-26 16:12',
    lastSeen: '2026-02-26 16:27'
  },
  {
    id: 'sip-005',
    name: '大连研发组-3F',
    workStatus: '忙碌',
    status: '启用',
    onlineStatus: '在线',
    address: '10.41.3.18:5060',
    createTime: '2026-01-13 14:10',
    lastLeaveMeetingTime: '2026-02-26 16:15',
    lastSeen: '2026-02-26 16:30'
  },
  {
    id: 'sip-006',
    name: '福州项目组-协作室',
    workStatus: '闲置',
    status: '启用',
    onlineStatus: '离线',
    address: '10.19.8.35:5060',
    createTime: '2026-01-18 10:55',
    lastLeaveMeetingTime: '2026-02-26 16:16',
    lastSeen: '2026-02-26 16:31'
  },
  {
    id: 'sip-007',
    name: '杭州客户中心-3号室',
    workStatus: '忙碌',
    status: '启用',
    onlineStatus: '在线',
    address: '10.57.3.26:5060',
    createTime: '2026-01-21 15:05',
    lastLeaveMeetingTime: '2026-02-26 16:18',
    lastSeen: '2026-02-26 16:33'
  },
  {
    id: 'sip-008',
    name: '济南区域中心-开会间',
    workStatus: '闲置',
    status: '启用',
    onlineStatus: '离线',
    address: '10.73.6.40:5060',
    createTime: '2026-01-24 08:45',
    lastLeaveMeetingTime: '2026-02-26 16:20',
    lastSeen: '2026-02-26 16:35'
  }
];

const SipDevices: React.FC = () => {
  const [devices, setDevices] = useState<SipDevice[]>(defaultDevices);
  const [filter, setFilter] = useState<FilterState>(initialFilter);
  const [renameModal, setRenameModal] = useState<RenameModalState | null>(null);
  const [renameError, setRenameError] = useState('');

  const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(prev => ({ ...prev, keyword: event.target.value }));
  };

  const handleRefreshHeartbeat = () => {
    const now = formatNow();
    setDevices(prev =>
      prev.map(device => ({
        ...device,
        lastSeen: device.status === '启用' ? now : '—'
      }))
    );
  };

  const handleOpenRenameModal = (id: string) => {
    const current = devices.find(device => device.id === id);
    if (!current) {
      return;
    }
    setRenameModal({ id: current.id, name: current.name });
    setRenameError('');
  };

  const handleCloseRenameModal = () => {
    setRenameModal(null);
    setRenameError('');
  };

  const handleRenameNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRenameModal(prev => (prev ? { ...prev, name: event.target.value } : prev));
    if (renameError) {
      setRenameError('');
    }
  };

  const handleRenameSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!renameModal) {
      return;
    }
    const trimmedName = renameModal.name.trim();
    if (!trimmedName) {
      setRenameError('设备名称不能为空');
      return;
    }
    setDevices(prev =>
      prev.map(device =>
        device.id === renameModal.id ? { ...device, name: trimmedName } : device
      )
    );
    handleCloseRenameModal();
  };

  const handleToggleStatus = (id: string) => {
    setDevices(prev =>
      prev.map(device =>
        device.id === id
          ? {
            ...device,
            status: device.status === '启用' ? '禁用' : '启用',
            onlineStatus: device.status === '启用' ? '离线' : '在线'
          }
          : device
      )
    );
  };

  const keyword = filter.keyword.trim().toLowerCase();
  const filteredDevices = devices.filter(device => {
    if (!keyword) {
      return true;
    }
    return [device.name, device.address].join(' ').toLowerCase().includes(keyword);
  });
  const {
    total,
    page,
    pageCount,
    pageSize,
    pagedRows,
    setPage,
    setPageSize
  } = useTablePagination(filteredDevices);

  return (
    <div className="page-block">
      <div className="breadcrumb">云会议 &gt; SIP设备</div>

      <div className="page-card">
        <div className="toolbar">
          <div className="toolbar-left">
            <input
              className="toolbar-input"
              name="keyword"
              value={filter.keyword}
              onChange={handleKeywordChange}
              placeholder="搜索设备名称 / 地址"
            />
            <button type="button" className="toolbar-icon" aria-label="搜索">
              <span className="icon icon-search" />
            </button>
            <button
              type="button"
              className="toolbar-icon"
              aria-label="刷新心跳"
              onClick={handleRefreshHeartbeat}
              data-testid="refresh-heartbeat"
            >
              <span className="icon icon-refresh" />
            </button>
          </div>
          <div className="toolbar-right">
            <span className="meta-text">设备由终端自动上报，暂不支持手动新增</span>
          </div>
        </div>

        <div className="table-container sip-device-table-container">
          <table className="compact-table sip-device-table">
            <thead>
              <tr>
                <th>设备名称</th>
                <th>状态</th>
                <th>启用状态</th>
                <th>在线状态</th>
                <th>地址</th>
                <th>创建时间</th>
                <th>最近离开会议时间</th>
                <th>最新心跳</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="table-empty">暂无匹配数据</td>
                </tr>
              ) : (
                pagedRows.map(device => (
                  <tr key={device.id}>
                    <td>{device.name}</td>
                    <td>
                      <span className={device.workStatus === '忙碌' ? 'badge badge-warning' : 'badge badge-success'}>
                        {device.workStatus}
                      </span>
                    </td>
                    <td>
                      <span className={device.status === '启用' ? 'badge badge-success' : 'badge badge-muted'}>
                        {device.status}
                      </span>
                    </td>
                    <td>
                      <span className={device.onlineStatus === '在线' ? 'badge badge-success' : 'badge badge-muted'}>
                        {device.onlineStatus}
                      </span>
                    </td>
                    <td>{device.address}</td>
                    <td>{device.createTime}</td>
                    <td>{device.lastLeaveMeetingTime}</td>
                    <td>{device.lastSeen}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="text-link"
                          onClick={() => handleOpenRenameModal(device.id)}
                        >
                          修改
                        </button>
                        <button
                          type="button"
                          className="text-link"
                          onClick={() => handleToggleStatus(device.id)}
                        >
                          {device.status === '启用' ? '禁用' : '启用'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          total={total}
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {renameModal && (
        <div className="modal-backdrop" onClick={handleCloseRenameModal}>
          <div className="modal sip-rename-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>修改设备</h3>
                <p className="meta-text">当前设备：{devices.find(device => device.id === renameModal.id)?.name || '-'}</p>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                aria-label="关闭"
                onClick={handleCloseRenameModal}
              >
                <span className="modal-close-icon" aria-hidden="true" />
              </button>
            </div>
            <form className="form-grid sip-rename-form" onSubmit={handleRenameSubmit}>
              <label className="form-field">
                <span>设备名称</span>
                <input
                  name="name"
                  value={renameModal.name}
                  onChange={handleRenameNameChange}
                  placeholder="请输入设备名称"
                />
                {renameError && <span className="field-error">{renameError}</span>}
              </label>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">保存</button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseRenameModal}>
                  取消
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SipDevices;
